/* eslint-disable @angular-eslint/no-input-rename */
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  AfterViewInit,
  ViewChild,
  ChangeDetectionStrategy,
  Input,
  EventEmitter,
  Output,
  NgZone,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Params, Router, Event } from '@angular/router';
import { configureScope } from '@sentry/browser';
import { EMPTY, Subscription, Observable, Subject, catchError, takeUntil } from 'rxjs';
import { UsersAPIService } from '@app/akita/api/users/services/user-api.service';
import { UserModel } from '@app/shared/models/api/user.model';
import { MetaService } from '@app/shared/services/meta.service';
import { TranslocoService, TranslocoModule } from '@ngneat/transloco';
import { WindowUtils } from '@app/shared/utils/window.util';
import {
  Validators,
  UntypedFormControl,
  ReactiveFormsModule,
  FormsModule,
} from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { AkitaAuthService } from '@app/akita/api/auth/state/auth.service';
import { AkitaScreenQuery } from '@app/akita/screen/state/screen.query';
import { ScreenSizeInfoState } from '@app/akita/screen/state/screen.store';
import { AkitaRouterQuery } from '@app/akita/router/state/router.query';
import { CheckEmailIconComponent } from '../../../shared/components/icons/generated/check-email/check-email.component';
import { LoadingComponent } from '../../../shared/components/loading/loading.component';
import { RemoveIconComponent } from '../../../shared/components/icons/generated/remove/remove.component';
import { NgIf, AsyncPipe } from '@angular/common';

@Component({
  selector: 'app-reset-password',
  templateUrl: './reset-password.template.html',
  styleUrls: ['./reset-password.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoModule,
    NgIf,
    RemoveIconComponent,
    ReactiveFormsModule,
    FormsModule,
    LoadingComponent,
    CheckEmailIconComponent,
    AsyncPipe,
  ],
})
export class ResetPasswordComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() public inDialog?: boolean | null = null;
  @Input('email') public placeholderEmail?: string | null = null;
  @Input('username') public placeholderUsername?: string | null = null;
  @Input('previous-mode') public previousMode?: string | null = null;
  @Input('skip-redirect') public skipRedirect?: boolean | null = null;

  @Output() public readonly handleRedirect: EventEmitter<void> = new EventEmitter();
  @Output() public readonly dialogModeChange: EventEmitter<any> = new EventEmitter();
  @Output() public readonly closeDialog: EventEmitter<boolean> = new EventEmitter();

  public readonly emailFromControl: UntypedFormControl;
  public redirect?: string | null = null;
  public submitted: boolean;
  public failed: boolean;
  public success: boolean;
  public loading: boolean;
  public isBrowser: boolean;

  public params: Params = {};
  public queryParams: Params = {};
  public routeData: Params = {};

  private readonly urlObserver: Subscription | null;
  private readonly langChangesObserver: Subscription | null;
  private userLoginObserver?: Subscription | null;

  public lessThan$: Observable<ScreenSizeInfoState>;
  public isRtlLayout$: Observable<boolean>;

  private readonly destroySubject: Subject<void>;

  @ViewChild('email') private readonly emailElement?: ElementRef | null;

  constructor(
    private readonly zone: NgZone,
    private readonly router: Router,
    private readonly meta: MetaService,
    private readonly translateService: TranslocoService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly usersApiService: UsersAPIService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly sanitizer: DomSanitizer,
    private readonly akitaAuthService: AkitaAuthService,
    private readonly akitaScreenQuery: AkitaScreenQuery,
    private readonly akitaRouterQuery: AkitaRouterQuery
  ) {
    this.destroySubject = new Subject<void>();
    this.isBrowser = this.akitaRouterQuery.isBrowser;
    this.submitted = false;
    this.loading = false;
    this.success = false;
    this.failed = false;

    this.lessThan$ = this.akitaScreenQuery.selectLessThan();
    this.isRtlLayout$ = this.akitaRouterQuery.selectIsRtlLayout();

    this.emailFromControl = new UntypedFormControl('', {
      validators: Validators.compose([Validators.required, Validators.email]),
      updateOn: 'change',
    });

    this.langChangesObserver = this.translateService.langChanges$
      .pipe(takeUntil(this.destroySubject))
      .subscribe({
        next: () => {
          const description = this.translateService.translate('app_title');
          this.meta.setTitle('title_login', true);
          this.meta.setCanonical();
          this.meta.setAlternateHreflangsTags(null);

          this.meta.setTags([
            { name: 'og:url', content: `${WindowUtils.getLocationHref() || ''}` },
            { name: 'og:type', content: `website` },
            { name: 'og:description', content: description },
            { name: 'description', content: description },
          ]);
        },
        error: () => {},
      });

    // Listen to Navigation End events
    this.urlObserver = this.router.events.pipe(takeUntil(this.destroySubject)).subscribe({
      next: ($event: Event) => {
        if ($event instanceof NavigationEnd) {
          let route = this.activatedRoute.snapshot;
          while (route.firstChild) {
            route = route.firstChild;
          }
          this.onNavigationEnd(
            route.params || {},
            route.queryParams || {},
            route.data || {}
          );
        }
      },
    });
  }

  /**
   * Method called when the component is initialized
   */
  public ngOnInit(): void {
    let route = this.activatedRoute.snapshot;
    while (route.firstChild) {
      route = route.firstChild;
    }
    this.onNavigationEnd(route.params || {}, route.queryParams || {}, route.data || {});
  }

  public ngAfterViewInit(): void {
    // Set Focus
    if (this.isBrowser) {
      this.zone.runOutsideAngular(() => {
        setTimeout(() => {
          if (this.emailElement?.nativeElement?.focus) {
            this.emailElement.nativeElement.focus();
          }
        }, 0);
      });
    }
  }

  /**
   * Method called when the view loads
   * @param params: URL Params
   * @param queryParams: URL Query Params
   * @param routeData: Route Data
   */
  public onNavigationEnd(params: Params, queryParams: Params, routeData?: Params): void {
    this.params = params || {};
    this.queryParams = queryParams || {};
    this.routeData = routeData || {};
    this.redirect = this.akitaRouterQuery.redirect;
    this.changeDetector.markForCheck();

    const email =
      this.queryParams.email || this.params.email || this.placeholderEmail || '';
    if (email) {
      if (this.emailFromControl) {
        this.emailFromControl.setValue(email);
        this.emailFromControl.markAsTouched();
      }
    }
  }

  /**
   * Method called when the Sign in (Popsy or Facebook) completes
   * @param user The user instance from the Backend
   */
  public loggedIn(user: UserModel): boolean {
    this.loading = false;
    if (user) {
      this.success = true;
      this.akitaAuthService.userSignedIn(user);
    } else {
      this.success = false;
    }
    this.changeDetector.markForCheck();

    this.handleRedirect.next();
    if (!this.skipRedirect) {
      this.redirect = decodeURI(this.queryParams.redirect || this.params.redirect || '');
      if (this.redirect && user && user.oauth && user.oauth.token) {
        WindowUtils.setLocationHref(`${this.redirect}${user.oauth.token.accessToken}`);
        return false;
      } else {
        let params = {
          ...this.akitaRouterQuery.queryParams,
        };
        if (this.redirect) {
          params = { ...params, redirect: encodeURI(this.redirect) };
        }

        this.router
          .navigate(this.akitaRouterQuery.homeUrlFragment, {
            queryParams: params,
          })
          .catch((error) => {
            console.log(error);
          });
      }
    }

    this.close(false);
    return true;
  }

  /**
   * Sign in with Popsy
   */
  public resetPassword(): void {
    if (this.emailFromControl?.status !== 'VALID') {
      return;
    }

    let email = '';
    if (this.emailFromControl) {
      email = this.emailFromControl.value;
    }

    this.loading = true;
    this.submitted = true;
    this.failed = false;
    this.success = false;

    configureScope((scope) => {
      scope.setUser({
        id: '?',
        username: '',
        email: email,
      });
    });

    this.userLoginObserver = this.usersApiService
      .initResetPassword(email)
      .pipe(
        catchError((error: unknown) => {
          const parsed = error as HttpErrorResponse;
          this.failed = true;
          this.loading = false;

          configureScope((scope) => {
            scope.setUser({});
          });

          let errorCode = '';
          if (parsed?.error?.error) {
            errorCode = parsed.error.error;
          }

          if (this.emailFromControl) {
            if (errorCode === 'USER_NOT_FOUND') {
              this.emailFromControl.setErrors({ USER_NOT_FOUND: true });
            } else if (errorCode === 'USER_BANNED') {
              this.emailFromControl.setErrors({ USER_BANNED: true });
            } else {
              this.emailFromControl.setErrors({ UNKNOWN: true });
            }
          }

          this.changeDetector.markForCheck();
          return EMPTY;
        }),
        takeUntil(this.destroySubject)
      )
      .subscribe({
        next: () => {
          this.submitted = true;
          this.success = true;
          this.changeDetector.markForCheck();
        },
        error: () => {},
      });
  }
  public get signUpQueryParams(): any {
    let params = {};
    if (this.emailFromControl?.status === 'VALID') {
      params = { ...params, email: this.emailFromControl.value };
    }
    const username = this.params.username || this.queryParams.username || '';
    if (username) {
      params = { ...params, username: username };
    }
    this.redirect = decodeURI(this.queryParams.redirect || this.params.redirect || '');
    if (this.redirect) {
      params = { ...params, redirect: encodeURI(this.redirect) };
    }
    return params;
  }

  public changeDialogMode(mode?: string | null): boolean {
    const username: string | null =
      this.params.username ||
      this.queryParams.username ||
      this.placeholderUsername ||
      null;
    let email: string | null = null;
    if (this.emailFromControl?.status === 'VALID') {
      email = this.emailFromControl.value;
    }

    this.dialogModeChange.emit({
      mode: mode || 'sign-in',
      email: email,
      username: username,
      previousMode: 'reset-password',
    });

    return false;
  }

  public close(dismiss?: boolean | null): boolean {
    this.closeDialog.emit(Boolean(dismiss));
    return false;
  }

  public get helpText(): SafeHtml {
    let helpStr = this.translateService.translate('contact_support_if_no_email_received');
    helpStr = helpStr.replace(
      'contact@popsy.app',
      '<a class="popsy-link" href="mailto:contact@popsy.app">contact@popsy.app</a>'
    );
    return this.sanitizer.bypassSecurityTrustHtml(`${helpStr}`);
  }

  public ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();

    if (this.urlObserver) {
      this.urlObserver.unsubscribe();
    }

    if (this.langChangesObserver) {
      this.langChangesObserver.unsubscribe();
    }

    if (this.userLoginObserver) {
      this.userLoginObserver.unsubscribe();
    }
  }
}
